import { getCsrfToken, setCsrfToken } from './csrf';
import { setSession, clearSession } from './session';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  /** Skip the auto session-redirect on 401 (used by the login route itself). */
  skip401Redirect?: boolean;
  /** Send credentials/cookies (default true). */
  credentials?: RequestCredentials;
}

export async function apiFetch<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  // Double-submit CSRF for any mutating verb.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: opts.credentials ?? 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts.skip401Redirect) {
    clearSession();
    setCsrfToken(null);
    if (typeof window !== 'undefined' && !window.location.hash.startsWith('#/login')) {
      window.location.hash = '#/login';
    }
  }

  const ct = res.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    if (isJson && data && typeof data === 'object' && 'error' in data) {
      const e = (data as { error: { code: string; message: string; details?: unknown } }).error;
      throw new ApiError(res.status, e.code, e.message, e.details);
    }
    throw new ApiError(res.status, 'http_error', `HTTP ${res.status}`);
  }

  return data as T;
}

export async function login(email: string, password: string): Promise<{ csrfToken: string }> {
  const data = await apiFetch<{
    user: { id: string; email: string; role: 'ADMIN' | 'ANALYST' | 'READER' };
    csrfToken: string;
  }>('/auth/login', {
    method: 'POST',
    body: { email, password },
    skip401Redirect: true,
  });
  setSession(data.user);
  setCsrfToken(data.csrfToken);
  return { csrfToken: data.csrfToken };
}

export async function logout(): Promise<void> {
  await apiFetch('/auth/logout', { method: 'POST' });
  clearSession();
  setCsrfToken(null);
}

export async function fetchMe(): Promise<{
  user: { id: string; email: string; role: 'ADMIN' | 'ANALYST' | 'READER' };
  csrfToken: string;
} | null> {
  try {
    const data = await apiFetch<{
      user: { id: string; email: string; role: 'ADMIN' | 'ANALYST' | 'READER' };
      csrfToken: string;
    }>('/auth/me', { skip401Redirect: true });
    setSession(data.user);
    setCsrfToken(data.csrfToken);
    return data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function acceptInvitation(token: string, password: string): Promise<void> {
  await apiFetch('/auth/invitations/accept', {
    method: 'POST',
    body: { token, password },
    skip401Redirect: true,
  });
}
